import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useBoardStore } from '../../../boardStore';
import { API_ROOT, sendRequest } from '../../../../supplementary_components/requests';
import { addNotification } from '../../../../supplementary_components/store';
import {
    MANUAL_STATUS_OPTIONS,
    displayedTruckStatus,
    lockedCode,
    truckStatusLabel,
    truckStatusMenuColor,
    truckStatusStyle,
} from '../../../truckStatus';

const Cell = styled.div`
    position: relative;
    min-width: 0;
    .pill {
        width: 100%;
        box-sizing: border-box;
        border: none;
        border-radius: 0.35rem;
        padding: 0.5rem 0.4rem;
        font-size: 0.85rem;
        font-weight: 700;
        line-height: 1.2;
        text-align: center;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        cursor: default;
        user-select: none;
        background: ${props => props.$background};
        color: ${props => props.$color};
    }
    .pill.editable {
        cursor: pointer;
        user-select: none;
    }
    .pill:disabled {
        cursor: wait;
        opacity: 0.75;
    }
    [data-theme="dark"] & .pill.atHome,
    [data-theme="dark"] & .pill.pending {
        background: ${props => props.$darkBackground || props.$background};
        color: ${props => props.$darkColor || props.$color};
    }
`;

const Menu = styled.div`
    position: fixed;
    z-index: 300;
    min-width: 9.5rem;
    padding: 0.25rem;
    background: var(--bg);
    border-radius: 0.4rem;
    box-shadow: var(--on-hover-visible-object-shadow);
    button {
        display: block;
        width: 100%;
        border: none;
        background: transparent;
        text-align: left;
        padding: 0.4rem 0.5rem;
        border-radius: 0.3rem;
        font-size: 0.85rem;
        font-weight: 700;
        cursor: pointer;
    }
    button:hover {
        background: rgba(var(--light-gray-GRB), 0.15);
    }
`;

function boardNowMs() {
    const live = useBoardStore.getState().getLiveCurrentTime?.();
    const ms = live && typeof live.toMillis === 'function' ? live.toMillis() : NaN;
    return Number.isFinite(ms) ? ms : Date.now();
}

export const MB_TRUCK_STATUS = ({ truck }) => {
    const statusClock = useBoardStore((s) => s.statusClock);
    const patchTruck = useBoardStore((s) => s.patchTruck);
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [menuPos, setMenuPos] = useState(null);
    const buttonRef = useRef(null);
    const menuRef = useRef(null);
    void statusClock;

    const shown = displayedTruckStatus(truck, boardNowMs());
    const style = truckStatusStyle(shown.code);
    const toneClass = shown.code === 'AT_HOME' ? 'atHome' : shown.code === 'PENDING' ? 'pending' : '';

    useEffect(() => {
        if (!open) return undefined;
        const close = (event) => {
            const target = event.target;
            if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
            setOpen(false);
        };
        const closeOnScroll = () => setOpen(false);
        document.addEventListener('mousedown', close);
        window.addEventListener('scroll', closeOnScroll, true);
        return () => {
            document.removeEventListener('mousedown', close);
            window.removeEventListener('scroll', closeOnScroll, true);
        };
    }, [open]);

    const choose = (code) => {
        if (saving || code === shown.code || lockedCode(truck?.tasks, boardNowMs())) {
            setOpen(false);
            return;
        }
        const truckId = String(truck.id);
        const previous = truck.manualBoardStatus ?? null;
        setSaving(true);
        setOpen(false);
        patchTruck(truckId, { manualBoardStatus: code });
        sendRequest(
            'PATCH',
            `${API_ROOT}trucks/manualBoardStatus/${truck.id}`,
            null,
            () => setSaving(false),
            () => {
                setSaving(false);
                const current = useBoardStore.getState().trucksById?.[truckId];
                if (lockedCode(current?.tasks, boardNowMs())) return;
                if (current?.manualBoardStatus === code) {
                    patchTruck(truckId, { manualBoardStatus: previous });
                }
                addNotification({ message: 'Could not update truck status', type: 'alert' });
            },
            { manualBoardStatus: code }
        );
    };

    const openMenu = (event) => {
        event.stopPropagation();
        if (!shown.editable || saving) return;
        const rect = buttonRef.current?.getBoundingClientRect();
        if (!rect) return;
        setMenuPos({ top: rect.bottom + 4, left: rect.left });
        setOpen(true);
    };

    return (
        <Cell
            $background={style.background}
            $color={style.color}
            $darkBackground={style.darkBackground}
            $darkColor={style.darkColor}
            onClick={(event) => event.stopPropagation()}
        >
            {shown.editable ? (
                <button
                    type="button"
                    ref={buttonRef}
                    className={`pill editable ${toneClass}`}
                    title={truckStatusLabel(shown.code)}
                    disabled={saving}
                    onClick={openMenu}
                >
                    {truckStatusLabel(shown.code)}
                </button>
            ) : (
                <div className={`pill ${toneClass}`} title={truckStatusLabel(shown.code)}>
                    {truckStatusLabel(shown.code)}
                </div>
            )}
            {open && shown.editable && menuPos ? (
                <Menu ref={menuRef} style={{ top: menuPos.top, left: menuPos.left }}>
                    {MANUAL_STATUS_OPTIONS.map((option) => {
                        const dark = document.documentElement.getAttribute('data-theme') === 'dark';
                        return (
                            <button
                                key={option.code}
                                type="button"
                                style={{ color: truckStatusMenuColor(option.code, dark) }}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    choose(option.code);
                                }}
                            >
                                {option.label}
                            </button>
                        );
                    })}
                </Menu>
            ) : null}
        </Cell>
    );
};
